/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { memo, ReactNode, useEffect, useRef, useState } from 'react'
import { useGeminiLiveAPIContext } from './GeminiLiveAPIContext'
import { AudioRecorder } from './lib/audio-recorder'

export type ControlTrayProps = {
  children?: ReactNode
}

function ControlTray({ children }: ControlTrayProps) {
  const [inVolume, setInVolume] = useState(0)
  const [audioRecorder] = useState(() => new AudioRecorder())
  const [muted, setMuted] = useState(false)
  const connectButtonRef = useRef<HTMLButtonElement>(null)
  const [textInput, setTextInput] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const { client, connected, connect, disconnect, volume } = useGeminiLiveAPIContext()

  useEffect(() => {
    if (!connected && connectButtonRef.current) {
      connectButtonRef.current.focus()
    }
  }, [connected])

  useEffect(() => {
    document.documentElement.style.setProperty('--volume', `${Math.max(5, Math.min(inVolume * 200, 8))}px`)
  }, [inVolume])

  useEffect(() => {
    const onData = (base64: string) => {
      client.sendRealtimeInput([
        {
          mimeType: 'audio/pcm;rate=16000',
          data: base64,
        },
      ])
    }
    if (connected && !muted && audioRecorder) {
      audioRecorder.on('data', onData).on('volume', setInVolume).start()
    } else {
      audioRecorder.stop()
    }
    return () => {
      audioRecorder.off('data', onData).off('volume', setInVolume)
    }
  }, [connected, client, muted, audioRecorder])

  const handleSubmit = () => {
    client.send([{ text: textInput }])
    setTextInput('')
    if (inputRef.current) {
      inputRef.current.innerText = ''
    }
  }

  return (
    <div>
      <div>
        <button onClick={() => setMuted(!muted)}>{!muted ? <span>mic-on</span> : <span>mic-off</span>}</button>
        <button ref={connectButtonRef} onClick={connected ? disconnect : connect}>
          <span>{connected ? 'disconnect' : 'connect'}</span>
        </button>
      </div>

      <div style={{ pointerEvents: !connected ? 'none' : 'auto' }}>
        <span>
          Volume: {Math.round(volume * 100)} / Connected: {connected ? '✅' : '❌'}
        </span>

        {children}
      </div>
      <div>
        <div className="input-content">
          <textarea
            className="input-area"
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                e.stopPropagation()
                handleSubmit()
              }
            }}
            onChange={(e) => setTextInput(e.target.value)}
            value={textInput}
          ></textarea>
          <button className="send-button material-symbols-outlined filled" onClick={handleSubmit}>
            Send
          </button>
        </div>
      </div>
    </div>
  )
}

export default memo(ControlTray)
